import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { LogOut, ArrowLeft, Search, Download, FileText, Mail, Phone, Plus, Trash2, X, Eye, EyeOff, Upload } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const VivierCandidatures = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [candidatures, setCandidatures] = useState([]);
  const [filteredCandidatures, setFilteredCandidatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Sorting states
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCandidature, setSelectedCandidature] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [showDeletePassword, setShowDeletePassword] = useState(false);

  // Form state for adding candidature
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    intitule_poste: '',
    societe: '',
    email: '',
    telephone: '',
    commentaire: ''
  });
  const [files, setFiles] = useState({
    cv: null,
    lettre: null
  });

  useEffect(() => {
    fetchVivierCandidatures();
  }, [sortBy, sortOrder]); // Re-fetch when sort parameters change

  useEffect(() => {
    filterCandidatures();
  }, [candidatures, searchQuery]);

  const fetchVivierCandidatures = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/candidatures/vivier?sort_by=${sortBy}&sort_order=${sortOrder}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const candidaturesData = await response.json();
        setCandidatures(candidaturesData);
      } else {
        toast.error('Erreur lors du chargement du vivier');
      }
    } catch (error) {
      console.error('Error fetching vivier candidatures:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const filterCandidatures = () => {
    if (!searchQuery.trim()) {
      setFilteredCandidatures(candidatures);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = candidatures.filter(candidature => 
      candidature.nom.toLowerCase().includes(query) ||
      candidature.prenom.toLowerCase().includes(query) ||
      candidature.job_title.toLowerCase().includes(query) ||
      candidature.job_company.toLowerCase().includes(query)
    );
    setFilteredCandidatures(filtered);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      // Toggle order if same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to desc
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) {
      return '↕️'; // Default sort icon
    }
    return sortOrder === 'desc' ? '↓' : '↑';
  };

  const handleLogout = () => {
    logout();
    toast.success('Déconnexion réussie');
  };

  const handleBackToRH = () => {
    navigate('/rh');
  };

  const handleDownloadFile = async (filename, type) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/download/${filename}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        toast.error('Erreur lors du téléchargement du fichier');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Erreur lors du téléchargement du fichier');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileChange = (type, file) => {
    if (!file) {
      setFiles(prev => ({
        ...prev,
        [type]: null
      }));
      return;
    }

    // Validation du format de fichier
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format de fichier non autorisé. Seuls les fichiers PDF, DOC et DOCX sont acceptés.');
      return;
    }

    // Validation de la taille (10 Mo max)
    const maxSize = 10 * 1024 * 1024; // 10 MB
    if (file.size > maxSize) {
      toast.error('Le fichier est trop volumineux. Taille maximum autorisée : 10 Mo.');
      return;
    }

    setFiles(prev => ({
      ...prev,
      [type]: file
    }));
  };

  const uploadFile = async (file) => {
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    const token = localStorage.getItem('token');
    const response = await fetch(`${API}/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: uploadFormData,
    });

    if (response.ok) {
      const result = await response.json();
      return result.filename;
    } else {
      throw new Error('Erreur lors de l\'upload du fichier');
    }
  };

  const handleAddCandidature = async () => {
    // Validation des champs obligatoires
    if (!formData.nom.trim()) {
      toast.error('Le nom est requis');
      return;
    }
    if (!formData.prenom.trim()) {
      toast.error('Le prénom est requis');
      return;
    }
    if (!formData.intitule_poste.trim()) {
      toast.error('L\'intitulé du poste est requis');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      let cvFilename = null;
      let lettreFilename = null;

      // Upload des fichiers si présents
      if (files.cv) {
        cvFilename = await uploadFile(files.cv);
      }
      if (files.lettre) {
        lettreFilename = await uploadFile(files.lettre);
      }

      // Créer la candidature dans le vivier
      const candidatureData = {
        nom: formData.nom.trim(),
        prenom: formData.prenom.trim(),
        intitule_poste: formData.intitule_poste.trim(),
        societe: formData.societe.trim() || null,
        email: formData.email.trim() || null,
        telephone: formData.telephone.trim() || null,
        commentaire: formData.commentaire.trim() || null,
        cv_filename: cvFilename,
        lettre_filename: lettreFilename
      };

      const response = await fetch(`${API}/candidatures/vivier`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(candidatureData),
      });

      if (response.ok) {
        toast.success('Candidature ajoutée au vivier avec succès');
        // Reset du formulaire
        setFormData({
          nom: '',
          prenom: '',
          intitule_poste: '',
          societe: '',
          email: '',
          telephone: '',
          commentaire: ''
        });
        setFiles({
          cv: null,
          lettre: null
        });
        setShowAddModal(false);
        fetchVivierCandidatures(); // Refresh the list
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Erreur lors de l\'ajout de la candidature');
      }
    } catch (error) {
      console.error('Error adding candidature:', error);
      toast.error('Erreur lors de l\'ajout de la candidature');
    }
  };

  const handleDeleteCandidature = async () => {
    if (!deletePassword) {
      toast.error('Veuillez saisir votre mot de passe');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/candidatures/vivier/${selectedCandidature.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          password: deletePassword
        }),
      });

      if (response.ok) {
        toast.success('Candidature supprimée avec succès');
        setShowDeleteModal(false);
        setSelectedCandidature(null);
        setDeletePassword('');
        fetchVivierCandidatures(); // Refresh the list
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Erreur lors de la suppression de la candidature');
      }
    } catch (error) {
      console.error('Error deleting candidature:', error);
      toast.error('Erreur de connexion');
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Company logos
  const companyLogos = {
    'Ma Conciergerie': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/mruq8p60_PNG_MA%20CONCIERGERIE.png',
    'Mon Evenementiel': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/o94kh96a_logo-monevenemenciel_png.png',
    'SISENS': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/gwqu631h_25.04.10%20SISENS%20LOGO.png',
    'Prysme': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/nzxlkuw3_logo-prysme-couleur.png'
  };

  return (
    <div className="vivier-page">
      {/* Header */}
      <header className="rh-header">
        <div className="flex items-center space-x-4">
          <Button
            onClick={handleBackToRH}
            variant="outline"
            size="sm"
            className="btn-outline"
            style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
          >
            <ArrowLeft size={16} className="mr-2" />
            Retour
          </Button>
          <img
            src={companyLogos['Prysme']}
            alt="Logo Prysme"
            className="rh-header-logo"
          />
        </div>
        <h1>Vivier des candidatures</h1>
        <Button
          onClick={handleLogout}
          variant="outline"
          size="sm"
          className="btn-outline"
          style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
        >
          <LogOut size={16} className="mr-2" />
          Déconnexion
        </Button>
      </header>

      {/* Main Content */}
      <main className="rh-main">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            {/* Search Bar and Add Button */}
            <div className="vivier-search-section">
              <div className="vivier-search-container">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <Input
                    type="text"
                    placeholder="Rechercher par nom, prénom ou poste..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="vivier-search-input pl-10"
                  />
                </div>
              </div>
              <div className="vivier-actions">
                <Button
                  onClick={() => setShowAddModal(true)}
                  className="btn-primary"
                  style={{ backgroundColor: '#eb5d2d', borderColor: '#eb5d2d' }}
                >
                  <Plus size={16} className="mr-2" />
                  Ajouter une candidature
                </Button>
              </div>
            </div>
            
            <div className="vivier-results-count">
              {filteredCandidatures.length} candidature{filteredCandidatures.length > 1 ? 's' : ''} trouvée{filteredCandidatures.length > 1 ? 's' : ''}
            </div>

            {/* Candidatures Table */}
            <Card className="vivier-card">
              <CardContent className="p-4">
                <h3 className="text-lg font-semibold mb-3">
                  Toutes les candidatures - Recrutements généraux
                </h3>
                <div className="vivier-table-container">
                  <table className="vivier-table">
                    <thead>
                      <tr>
                        <th 
                          className="sortable-header" 
                          onClick={() => handleSort('job_company')}
                          style={{ cursor: 'pointer' }}
                        >
                          Intitulé du poste {getSortIcon('job_company')}
                        </th>
                        <th 
                          className="sortable-header" 
                          onClick={() => handleSort('nom')}
                          style={{ cursor: 'pointer' }}
                        >
                          Candidat {getSortIcon('nom')}
                        </th>
                        <th>Documents</th>
                        <th>Coordonnées</th>
                        <th>Commentaire</th>
                        <th 
                          className="sortable-header" 
                          onClick={() => handleSort('created_at')}
                          style={{ cursor: 'pointer' }}
                        >
                          Date {getSortIcon('created_at')}
                        </th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCandidatures.map((candidature) => (
                        <tr key={candidature.id}>
                          <td>
                            <div className="vivier-job-info">
                              <div className="vivier-job-title">
                                {candidature.job_company} : {candidature.job_title}
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="vivier-candidat-name">
                              {candidature.prenom} {candidature.nom}
                            </div>
                          </td>
                          <td>
                            <div className="vivier-files">
                              {candidature.cv_filename ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="mr-1 text-xs px-2 py-1"
                                  onClick={() => handleDownloadFile(candidature.cv_filename, 'CV')}
                                >
                                  <Download size={12} className="mr-1" />
                                  Télécharger CV
                                </Button>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="mr-1 text-xs px-2 py-1"
                                  disabled
                                >
                                  <Download size={12} className="mr-1" />
                                  Fichier indisponible
                                </Button>
                              )}
                              {candidature.lettre_filename ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-xs px-2 py-1"
                                  onClick={() => handleDownloadFile(candidature.lettre_filename, 'Lettre')}
                                >
                                  <FileText size={12} className="mr-1" />
                                  Télécharger Lettre
                                </Button>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-xs px-2 py-1"
                                  disabled
                                >
                                  <FileText size={12} className="mr-1" />
                                  Fichier indisponible
                                </Button>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="vivier-contact">
                              {candidature.email && (
                                <div className="vivier-contact-item">
                                  <Mail size={12} />
                                  <span>{candidature.email}</span>
                                </div>
                              )}
                              {candidature.telephone && (
                                <div className="vivier-contact-item">
                                  <Phone size={12} />
                                  <span>{candidature.telephone}</span>
                                </div>
                              )}
                              {!candidature.email && !candidature.telephone && (
                                <span className="text-gray-400 text-xs">Non renseigné</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="vivier-comment">
                              {candidature.commentaire || '-'}
                            </div>
                          </td>
                          <td>
                            <div className="vivier-date">
                              {formatDate(candidature.created_at)}
                            </div>
                          </td>
                          <td>
                            <Button
                              onClick={() => {
                                setSelectedCandidature(candidature);
                                setShowDeleteModal(true);
                              }}
                              variant="destructive"
                              size="sm"
                              className="btn-delete-compact"
                            >
                              <Trash2 size={12} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {filteredCandidatures.length === 0 && (
                  <div className="empty-state-compact">
                    {searchQuery ? (
                      <p>Aucune candidature trouvée pour "{searchQuery}"</p>
                    ) : (
                      <p>Aucune candidature dans le vivier</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>

      {/* Add Candidature Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <Card className="modal-card" style={{ border: '2px solid #eb5d2d' }}>
              <CardHeader className="modal-header" style={{ background: 'white' }}>
                <div className="flex justify-between items-center">
                  <CardTitle className="modal-title" style={{ color: 'black' }}>
                    <Plus size={20} className="mr-2" style={{ color: '#eb5d2d' }} />
                    Ajouter une candidature au vivier
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAddModal(false)}
                    className="modal-close"
                  >
                    <X size={20} />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="modal-content" style={{ background: 'white' }}>
                <div className="form-container">
                  {/* Nom et Prénom */}
                  <div className="form-row">
                    <div className="form-group">
                      <Label htmlFor="nom" className="form-label" style={{ color: 'black' }}>
                        Nom *
                      </Label>
                      <Input
                        id="nom"
                        type="text"
                        value={formData.nom}
                        onChange={(e) => handleInputChange('nom', e.target.value)}
                        className="form-input"
                        placeholder="Nom du candidat"
                        style={{ color: 'black' }}
                      />
                    </div>
                    <div className="form-group">
                      <Label htmlFor="prenom" className="form-label" style={{ color: 'black' }}>
                        Prénom *
                      </Label>
                      <Input
                        id="prenom"
                        type="text"
                        value={formData.prenom}
                        onChange={(e) => handleInputChange('prenom', e.target.value)}
                        className="form-input"
                        placeholder="Prénom du candidat"
                        style={{ color: 'black' }}
                      />
                    </div>
                  </div>

                  {/* Intitulé du poste */}
                  <div className="form-group">
                    <Label htmlFor="intitule_poste" className="form-label" style={{ color: 'black' }}>
                      Intitulé du poste pour le candidat *
                    </Label>
                    <Input
                      id="intitule_poste"
                      type="text"
                      value={formData.intitule_poste}
                      onChange={(e) => handleInputChange('intitule_poste', e.target.value)}
                      className="form-input"
                      placeholder="Ex: Développeur Full Stack"
                      style={{ color: 'black' }}
                    />
                  </div>

                  {/* Société */}
                  <div className="form-group">
                    <Label htmlFor="societe" className="form-label" style={{ color: 'black' }}>
                      Société
                    </Label>
                    <select 
                      id="societe"
                      value={formData.societe} 
                      onChange={(e) => handleInputChange('societe', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      style={{ color: 'black' }}
                    >
                      <option value="">Sélectionnez une société</option>
                      <option value="Ma Conciergerie">Ma Conciergerie</option>
                      <option value="Mon Evenementiel">Mon Evenementiel</option>
                      <option value="SISENS">SISENS</option>
                      <option value="Horizon Solution">Horizon Solution</option>
                      <option value="Prysme">Prysme</option>
                    </select>
                  </div>

                  {/* Documents Upload */}
                  <div className="form-row">
                    <div className="form-group">
                      <Label htmlFor="cv" className="form-label" style={{ color: 'black' }}>
                        CV
                      </Label>
                      <div className="file-upload-container">
                        <Input
                          id="cv"
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={(e) => handleFileChange('cv', e.target.files[0])}
                          className="file-input"
                        />
                        {files.cv && (
                          <div className="file-info">
                            <Upload size={16} className="mr-2" />
                            <span className="file-name">{files.cv.name}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="form-group">
                      <Label htmlFor="lettre" className="form-label" style={{ color: 'black' }}>
                        Lettre de motivation
                      </Label>
                      <div className="file-upload-container">
                        <Input
                          id="lettre"
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={(e) => handleFileChange('lettre', e.target.files[0])}
                          className="file-input"
                        />
                        {files.lettre && (
                          <div className="file-info">
                            <Upload size={16} className="mr-2" />
                            <span className="file-name">{files.lettre.name}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Coordonnées */}
                  <div className="form-row">
                    <div className="form-group">
                      <Label htmlFor="email" className="form-label" style={{ color: 'black' }}>
                        E-mail
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="form-input"
                        placeholder="email@exemple.com"
                        style={{ color: 'black' }}
                      />
                    </div>
                    <div className="form-group">
                      <Label htmlFor="telephone" className="form-label" style={{ color: 'black' }}>
                        Numéro de téléphone
                      </Label>
                      <Input
                        id="telephone"
                        type="tel"
                        value={formData.telephone}
                        onChange={(e) => handleInputChange('telephone', e.target.value)}
                        className="form-input"
                        placeholder="06 12 34 56 78"
                        style={{ color: 'black' }}
                      />
                    </div>
                  </div>

                  {/* Commentaire */}
                  <div className="form-group">
                    <Label htmlFor="commentaire" className="form-label" style={{ color: 'black' }}>
                      Commentaire
                    </Label>
                    <Textarea
                      id="commentaire"
                      value={formData.commentaire}
                      onChange={(e) => handleInputChange('commentaire', e.target.value)}
                      className="form-textarea"
                      placeholder="Commentaires sur le candidat..."
                      rows={3}
                      style={{ color: 'black' }}
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="modal-actions">
                  <Button
                    onClick={handleAddCandidature}
                    className="btn-primary"
                    style={{ backgroundColor: '#eb5d2d', borderColor: '#eb5d2d' }}
                  >
                    Envoyer
                  </Button>
                  <Button
                    onClick={() => setShowAddModal(false)}
                    variant="outline"
                    className="btn-cancel"
                    style={{ color: 'black', borderColor: '#eb5d2d' }}
                  >
                    Annuler
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Delete Candidature Modal */}
      {showDeleteModal && selectedCandidature && (
        <div className="modal-overlay">
          <div className="modal-container">
            <Card className="modal-card">
              <CardHeader className="modal-header">
                <div className="flex justify-between items-center">
                  <CardTitle className="modal-title text-red-600">
                    <Trash2 size={20} className="mr-2" />
                    Supprimer la candidature
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowDeleteModal(false);
                      setSelectedCandidature(null);
                      setDeletePassword('');
                    }}
                    className="modal-close"
                  >
                    <X size={20} />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="modal-content">
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 font-medium mb-2">
                    Attention ! Cette action est irréversible.
                  </p>
                  <p className="text-red-700 text-sm">
                    Vous êtes sur le point de supprimer définitivement la candidature de :
                  </p>
                  <p className="text-red-800 font-medium mt-1">
                    {selectedCandidature.prenom} {selectedCandidature.nom} - {selectedCandidature.intitule_poste}
                  </p>
                </div>

                <div className="form-group">
                  <Label htmlFor="deletePassword" className="form-label">
                    Confirmez avec votre mot de passe RH *
                  </Label>
                  <div className="relative">
                    <Input
                      id="deletePassword"
                      type={showDeletePassword ? "text" : "password"}
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      className="form-input pr-10"
                      placeholder="Votre mot de passe RH"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowDeletePassword(!showDeletePassword)}
                    >
                      {showDeletePassword ? (
                        <EyeOff size={16} className="text-gray-400" />
                      ) : (
                        <Eye size={16} className="text-gray-400" />
                      )}
                    </Button>
                  </div>
                </div>

                {/* Modal Actions */}
                <div className="modal-actions">
                  <Button
                    onClick={handleDeleteCandidature}
                    className="btn-danger"
                    style={{ backgroundColor: '#ef4444', borderColor: '#ef4444' }}
                  >
                    <Trash2 size={16} className="mr-2" />
                    Supprimer définitivement
                  </Button>
                  <Button
                    onClick={() => {
                      setShowDeleteModal(false);
                      setSelectedCandidature(null);
                      setDeletePassword('');
                    }}
                    variant="outline"
                    className="btn-cancel"
                  >
                    Annuler
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default VivierCandidatures;